import { useState } from "react";
import { api } from "../api.ts";
import { colors, sans, serif, tokens } from "../theme.ts";
import { BottomSheet } from "./BottomSheet.tsx";
import { GlassButton, GlassFloat, inputStyle } from "./glass.tsx";

// What was typed and not yet saved is kept on this device, so a note that did
// not go through (no signal) or a sheet closed by mistake is still there.
const DRAFT = "mc2.journalDraft";
const readDraft = () => { try { return localStorage.getItem(DRAFT) ?? ""; } catch { return ""; } };
const writeDraft = (v: string) => { try { v ? localStorage.setItem(DRAFT, v) : localStorage.removeItem(DRAFT); } catch { /* no storage, no draft */ } };

/** The 48px glass circle above the nav, on the right. It opens the journal capture on every screen. */
export function CaptureButton({ onClick }: { onClick: () => void }) {
  return (
    <div style={{
      position: "fixed", right: `max(${tokens.space.dockInset}px, env(safe-area-inset-right))`,
      bottom: "calc(var(--nav-clear) + 12px)", zIndex: 25,
    }}>
      <GlassFloat style={{ width: tokens.size.fab, height: tokens.size.fab, display: "flex" }}>
        <button
          onClick={onClick} aria-label="Write in the journal"
          style={{
            flex: 1, border: "none", background: "transparent", borderRadius: 999, cursor: "pointer",
            color: colors.ink, display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
      </GlassFloat>
    </div>
  );
}

/** One text box. No title, no tags, no date: the entry is the text, and the server names the file. */
export function CaptureSheet({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [text, setText] = useState(readDraft);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const change = (v: string) => { setText(v); writeDraft(v); };
  const save = async () => {
    if (!text.trim() || busy) return;
    setBusy(true); setError(null);
    try {
      await api.journal(text);
      writeDraft("");
      onSaved();
      onClose();
    } catch (e: any) { setError(e?.message ?? "that did not go through"); setBusy(false); }
  };

  return (
    <BottomSheet onClose={onClose}>
      <div style={{ fontFamily: serif, fontSize: tokens.type.sheetTitle.size, lineHeight: tokens.type.sheetTitle.lineHeight, color: colors.ink, marginBottom: 12 }}>
        Journal
      </div>
      <textarea
        autoFocus value={text} onChange={(e) => change(e.target.value)} rows={7} placeholder="Anything"
        style={{ ...inputStyle, fontFamily: sans, resize: "none" }}
      />
      {error && <p style={{ margin: "8px 0 0", fontSize: 13, color: colors.ink }}>{error}. What you wrote is still here.</p>}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
        <GlassButton variant="primary" disabled={busy || !text.trim()} onClick={save}>{busy ? "…" : "Save"}</GlassButton>
      </div>
    </BottomSheet>
  );
}
